import UndoIcon from "@mui/icons-material/Undo";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import React from "react";

/** The name the Undo control announces and shows, on every proposal page. */
export const FIELD_UNDO_LABEL = "Undo Changes";

const UndoButton = styled(IconButton)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(0.5),
}));

type FieldUndoButtonProps = {
  onUndo: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Why this Undo cannot be done yet. Given, the button is drawn disabled, and its tooltip says why
   * instead of naming the control (#6995).
   */
  disabledReason?: string;
};

/**
 * The control that puts one proposal field back to the value it had before this page changed it.
 *
 * An active proposal's redline inputs and a draft's plain inputs both draw this one control, beside
 * the field's history control, so the two pages offer Undo the same way in the same place (#6873).
 * The caller decides when the field differs from its saved value; this only draws the button.
 */
export default function FieldUndoButton({ onUndo, disabledReason }: Readonly<FieldUndoButtonProps>) {
  const disabled = Boolean(disabledReason);
  return (
    <Tooltip title={disabledReason ?? FIELD_UNDO_LABEL}>
      <span>
        <UndoButton aria-label={FIELD_UNDO_LABEL} disabled={disabled} onClick={onUndo}>
          <UndoIcon color={disabled ? "disabled" : "secondary"} />
        </UndoButton>
      </span>
    </Tooltip>
  );
}
